/**
 * useAuthSecurity Hook
 *
 * Provides automatic idle timeout, session validation, and JWT checking
 */

import { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { logout } from 'store/authSlice';
import {
  IdleTimeoutManager,
  SessionMonitor,
  isTokenExpired,
  authStorage
} from 'utils/authSecurity';

interface UseAuthSecurityOptions {
  enableIdleTimeout?: boolean;
  enableSessionMonitoring?: boolean;
  enableJWTValidation?: boolean;
}

export const useAuthSecurity = (options: UseAuthSecurityOptions = {}) => {
  const {
    enableIdleTimeout = true,
    enableSessionMonitoring = true,
    enableJWTValidation = true
  } = options;

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const idleManagerRef = useRef<IdleTimeoutManager | null>(null);
  const sessionMonitorRef = useRef<SessionMonitor | null>(null);

  // Handle session invalidation
  const handleSessionInvalid = () => {
    console.log('Session invalid - logging out');

    // Clear storage
    authStorage.clear();

    // Dispatch logout action
    dispatch(logout());

    // Redirect to login
    navigate('/login', { replace: true });
  };

  useEffect(() => {
    // Only run if user is logged in
    const accessToken = authStorage.getAccessToken();
    if (!accessToken) return;

    // 1. JWT Validation on mount
    if (enableJWTValidation && isTokenExpired(accessToken)) {
      console.log('Access token expired on mount');
      handleSessionInvalid();
      return;
    }

    // 2. Start Idle Timeout Manager
    if (enableIdleTimeout) {
      idleManagerRef.current = new IdleTimeoutManager(handleSessionInvalid);
      idleManagerRef.current.start();
    }

    // 3. Start Session Monitor
    if (enableSessionMonitoring) {
      sessionMonitorRef.current = new SessionMonitor(handleSessionInvalid);
      sessionMonitorRef.current.start();
    }

    // Cleanup on unmount
    return () => {
      if (idleManagerRef.current) {
        idleManagerRef.current.stop();
      }
      if (sessionMonitorRef.current) {
        sessionMonitorRef.current.stop();
      }
    };
  }, [enableIdleTimeout, enableSessionMonitoring, enableJWTValidation]);

  return {
    isIdle: () => idleManagerRef.current?.isIdle() ?? false,
    getIdleTime: () => idleManagerRef.current?.getIdleTime() ?? 0
  };
};
